import { FlexBox } from '@dixa/lib-shared-habla';
import {
  columnOrderingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  metaHelper,
  tableFeatures,
} from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';

import { AvatarPlaceholder } from './components/placeholders/avatarPlaceholder/avatarPlaceholder';
import { TextPlaceholder } from './components/placeholders/textPlaceholder/textPlaceholder';
import { ConversationsTablePreferences } from './components/preferences/conversationsTablePreferences';
import { ConversationsTableActions } from './components/rows/conversationRow/components/rowCells/actions/conversationsTableActions';
import { ConversationsTableAssignedAgent } from './components/rows/conversationRow/components/rowCells/assignedAgent/conversationsTableAssignedAgent';
import { ConversationsTableChannel } from './components/rows/conversationRow/components/rowCells/channel/conversationsTableChannel';
import { ConversationsTableCsid } from './components/rows/conversationRow/components/rowCells/csid/conversationsTableCsid';
import { ConversationsTableCustomer } from './components/rows/conversationRow/components/rowCells/customer/conversationsTableCustomer';
import { ConversationsTableQueue } from './components/rows/conversationRow/components/rowCells/queue/conversationsTableQueue';
import { ConversationsTableStatus } from './components/rows/conversationRow/components/rowCells/status/conversationsTableStatus';
import { ConversationsTableTimestamp } from './components/rows/conversationRow/components/rowCells/timestamp/conversationsTableTimestamp';
import {
  type ConversationsTableColumnMeta,
  type ConversationsTableMeta,
  type ConversationsTableSortableField,
} from './conversationsTable.types';

/** TanStack features registered on the conversations table. */
export const conversationsTableFeatures = tableFeatures({
  columnOrderingFeature,
  columnVisibilityFeature,
  columnMeta: metaHelper<ConversationsTableColumnMeta>(),
  tableMeta: metaHelper<ConversationsTableMeta>(),
});

export type ConversationsTableFeatures = typeof conversationsTableFeatures;

const columnHelper = createColumnHelper<
  ConversationsTableFeatures,
  SearchConversationEnriched
>();

/** The columns of the conversations table, in their default order. */
export const conversationsTableColumns = columnHelper.columns([
  columnHelper.display({
    id: 'conversation',
    enableHiding: false,
    header: 'Conversation',
    cell: () => <ConversationsTableCsid />,
    meta: {
      label: 'Conversation',
      width: '128px',
      placeholder: {
        header: '74px',
        cell: <TextPlaceholder height="24px" borderRadius="100" />,
      },
    },
  }),
  columnHelper.display({
    id: 'customer',
    enableHiding: false,
    header: 'Customer',
    cell: () => <ConversationsTableCustomer />,
    meta: {
      label: 'Customer',
      width: '32%',
      placeholder: {
        header: '60px',
        cell: (
          <FlexBox gap="300" alignItems="center">
            <AvatarPlaceholder />
            <TextPlaceholder width="60%" />
          </FlexBox>
        ),
      },
    },
  }),
  columnHelper.display({
    id: 'queue',
    header: 'Queue',
    cell: () => <ConversationsTableQueue />,
    meta: {
      label: 'Queue',
      width: '13%',
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: () => <ConversationsTableStatus />,
    meta: {
      label: 'Status',
      width: '160px',
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="50%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'channel',
    header: 'Channel',
    cell: () => <ConversationsTableChannel />,
    meta: {
      label: 'Channel',
      width: '10%',
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'assignedAgent',
    header: 'Assigned agent',
    cell: () => <ConversationsTableAssignedAgent />,
    meta: {
      label: 'Assigned agent',
      width: '15%',
      placeholder: {
        header: '88px',
        cell: (
          <FlexBox gap="300" alignItems="center">
            <AvatarPlaceholder />
            <TextPlaceholder width="50%" />
          </FlexBox>
        ),
      },
    },
  }),
  columnHelper.display({
    id: 'lastMessage',
    header: 'Last message',
    cell: () => (
      <ConversationsTableTimestamp timestampKey="last_message_created_at" />
    ),
    meta: {
      label: 'Last message',
      width: '10%',
      sortBy:
        'last_message_created_at' satisfies ConversationsTableSortableField,
      tooltip: 'Based on customer, agent, and automated messages.',
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="80%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'created',
    header: 'Created',
    cell: () => <ConversationsTableTimestamp timestampKey="created_at" />,
    meta: {
      label: 'Created',
      width: '10%',
      sortBy: 'created_at' satisfies ConversationsTableSortableField,
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="80%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'actions',
    enableHiding: false,
    header: ({ table }) => <ConversationsTablePreferences table={table} />,
    cell: () => <ConversationsTableActions />,
    meta: { width: '110px' },
  }),
]);
